'use client';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Mail, Phone, Users, Calendar, Cake, Medal, TrendingUp } from 'lucide-react';
import type { Player } from '@/types/tournament.model';
import { getGenderLabel } from '@/utils/gender-utils';
import type { Gender } from '@/types';
import {
  formatBirthdateForDisplay,
  getAgeFromBirthdateIso,
} from '@/utils/formatters';
import type { EloCategory } from './models/elo.model';
import { formatElo } from './utils/format-elo';
import { getCategoryByElo } from './utils/get-category-by-elo';

interface PlayerInfoPopoverProps {
  player: Player;
  children: React.ReactNode;
  /** When set with `eloCategories`, shows category / Elo for this sport from `player.rankings`. */
  sportUid?: string;
  eloCategories?: EloCategory[];
}

export function PlayerInfoPopover({
  player,
  children,
  sportUid,
  eloCategories = [],
}: PlayerInfoPopoverProps) {
  const getInitials = (name: string) => {
    const parts = name.split(' ').filter(Boolean);
    if (parts.length === 0) return 'U';
    const firstInitial = parts[0].charAt(0).toUpperCase();
    if (parts.length === 1) return firstInitial;
    const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
    return `${firstInitial}${lastInitial}`;
  };

  const formatWhatsAppNumber = (phone: string): string => {
    return phone.replace(/\D/g, '');
  };

  const birthLabel = formatBirthdateForDisplay(player.birthdate);
  const age = getAgeFromBirthdateIso(player.birthdate);

  const sportRanking =
    sportUid != null && sportUid !== ''
      ? player.rankings?.find((r) => r.sport?.uid === sportUid)
      : undefined;

  const eloNumeric = sportRanking != null ? formatElo(sportRanking.elo) : null;
  const categoryName =
    sportRanking != null && eloCategories.length > 0
      ? getCategoryByElo(eloCategories, sportRanking.elo)
      : null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="cursor-pointer focus:outline-none">
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="p-4">
          {/* Header with avatar and name */}
          <div className="flex items-center gap-3 mb-4">
            {player.profileImageUrl ? (
              <img
                src={player.profileImageUrl}
                alt={player.name}
                className="h-14 w-14 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 text-lg font-semibold text-blue-600">
                {getInitials(player.name)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 truncate">
                {player.name}
              </h4>
              {player.gender && (
                <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                  <Users className="h-3 w-3" />
                  {getGenderLabel(player.gender as Gender)}
                </span>
              )}
            </div>
          </div>

          {/* Contact info */}
          <div className="space-y-2 text-sm">
            {player.email && (
              <div className="flex items-center gap-2 text-gray-600">
                <Mail className="h-4 w-4 text-gray-400" />
                <span className="truncate">{player.email}</span>
              </div>
            )}
            {player.cellphone && (
              <div className="flex items-center gap-2 text-gray-600">
                <Phone className="h-4 w-4 text-gray-400" />
                <span>{player.cellphone}</span>
              </div>
            )}
            {birthLabel && (
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="h-4 w-4 shrink-0 text-gray-400" />
                <span>Nac. {birthLabel}</span>
              </div>
            )}
            {age != null && (
              <div className="flex items-center gap-2 text-gray-600">
                <Cake className="h-4 w-4 shrink-0 text-gray-400" />
                <span>{age} años</span>
              </div>
            )}
            {categoryName && (
              <div className="flex items-center gap-2 text-gray-600">
                <Medal className="h-4 w-4 shrink-0 text-gray-400" />
                <span>{categoryName}</span>
              </div>
            )}
            {eloNumeric != null && (
              <div className="flex items-center gap-2 text-gray-600">
                <TrendingUp className="h-4 w-4 shrink-0 text-gray-400" />
                <span>Elo {eloNumeric}</span>
              </div>
            )}
          </div>

          {/* Action buttons */}
          {(player.cellphone || player.email) && (
            <div className="flex gap-2 mt-4 pt-3 border-t">
              {player.cellphone && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  asChild
                >
                  <a
                    href={`https://wa.me/${formatWhatsAppNumber(player.cellphone)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="/assets/icons/whatsapp-icon.svg"
                      alt="WhatsApp"
                      className="h-4 w-4 mr-1"
                      style={{
                        filter:
                          'invert(42%) sepia(93%) saturate(1352%) hue-rotate(87deg) brightness(119%) contrast(119%)',
                      }}
                    />
                    WhatsApp
                  </a>
                </Button>
              )}
              {player.email && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  asChild
                >
                  <a href={`mailto:${player.email}`}>
                    <Mail className="h-4 w-4 mr-1" />
                    Email
                  </a>
                </Button>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

